import { useReporting } from '@/hooks/useReporting';
import useRouteInfo from '@/hooks/useRouteInfo';
import { useStore } from '@/providers/app-store-provider';
import { ProviderType } from '@/reporting/types';
import logger from '@/utils/client-logger';
import { env } from '@/utils/env';
import { priceInfo as getPriceInfo } from '@/utils/price';
import { getCurrentScrollPercentage, getViewArea } from '@/utils/scroll';
import md5 from 'crypto-js/md5';
import { useCallback, useEffect, useRef } from 'react';

export function useSpringSaleTracking() {
  const { trackEvent } = useReporting();
  const { getFromRouterInfo } = useRouteInfo();
  const currency = useStore((state) => state.currency);
  const userInfo = useStore((state) => state.userInfo);

  const enterTimer = useRef<number>(Date.now());
  // 记录最后的滚动状态，避免页面跳转时滚动位置被重置
  const lastScrollState = useRef({ viewAdvance: 0, viewArea: 1 });

  // 生成活动标识 MD5
  const marketActivityId = md5('SpringSale').toString();

  /**
   * @TrackType sensorsdata
   * @EventType CampaignPage_View
   * @EventDesc 202603春促主会场页面浏览
   * @EventParams {
   *   page_name: 页面名称,
   *   refer_page_name: 前向页面名称,
   *   refer_url_path: 前向页面路径,
   * }
   */
  const trackPageView = useCallback(() => {
    const { refer_page_name, refer_url_path } = getFromRouterInfo();
    enterTimer.current = Date.now();

    try {
      trackEvent(
        {
          eventName: 'CampaignPage_View',
          properties: {
            page_name: '202603春促主会场',
            refer_page_name,
            refer_url_path,
            market_activity_id: marketActivityId,
            market_activity_name: '26年3月春促活动',
          },
        },
        [ProviderType.SENSORS],
      );
    } catch (error) {
      logger.error('Spring Sale page view tracking error:', error);
    }
  }, [trackEvent, getFromRouterInfo]);

  /**
   * @TrackType sensorsdata
   * @EventType CampaignPage_Leave
   * @EventDesc 202603春促主会场页面离开
   * @EventParams {
   *   page_name: 页面名称,
   *   view_advance: 浏览进度,
   *   view_duration: 浏览时长,
   *   view_area: 浏览区域,
   * }
   */
  const trackPageLeave = useCallback(() => {
    // 使用保存的滚动状态
    const { viewAdvance, viewArea } = lastScrollState.current;

    try {
      trackEvent(
        {
          eventName: 'CampaignPage_Leave',
          properties: {
            page_name: '202603春促主会场',
            view_advance: viewAdvance,
            view_duration: Math.ceil((Date.now() - enterTimer.current) / 1000),
            view_area: viewArea,
            market_activity_id: marketActivityId,
            market_activity_name: '26年3月春促活动',
          },
        },
        [ProviderType.SENSORS],
      );
    } catch (error) {
      logger.error('Spring Sale page leave tracking error:', error);
    }
  }, [trackEvent]);

  /**
   * @TrackType sensorsdata
   * @EventType CampaignPageNavTab_Click
   * @EventDesc 202603春促主会场tab导航点击
   * @EventParams {
   *   page_name: 页面名称,
   *   module_name: 模块名称,
   *   pits_index: 坑位排序,
   *   tab_name: tab按钮名称,
   * }
   */
  const trackNavTabClick = useCallback(
    (tabName: string, index: number) => {
      try {
        trackEvent(
          {
            eventName: 'CampaignPageNavTab_Click',
            properties: {
              page_name: '202603春促主会场',
              module_name: '导航位',
              pits_index: index,
              tab_name: tabName,
              market_activity_id: marketActivityId,
              market_activity_name: '26年3月春促活动',
            },
          },
          [ProviderType.SENSORS],
        );
      } catch (error) {
        logger.error('Spring Sale nav tab click tracking error:', error);
      }
    },
    [trackEvent],
  );

  /**
   * @TrackType sensorsdata
   * @EventType CampaignPageViewMoreBtn_Click
   * @EventDesc 202603春促主会场view more按钮点击
   * @EventParams {
   *   page_name: 页面名称,
   *   module_name: 模块名称,
   *   pits_index: 坑位排序,
   * }
   */
  const trackViewMoreClick = useCallback(
    (tabName: string) => {
      try {
        trackEvent(
          {
            eventName: 'CampaignPageViewMoreBtn_Click',
            properties: {
              page_name: '202603春促主会场',
              tab_name: tabName,
              module_name: '导航位',
              market_activity_id: marketActivityId,
              market_activity_name: '26年3月春促活动',
            },
          },
          [ProviderType.SENSORS],
        );
      } catch (error) {
        logger.error('Spring Sale view more click tracking error:', error);
      }
    },
    [trackEvent],
  );

  /**
   * @TrackType sensorsdata
   * @EventType CampaignPageBanner_Click
   * @EventDesc 202603春促主会场banner点击
   * @EventParams {
   *   page_name: 页面名称,
   *   module_name: 模块名称,
   *   pits_index: 坑位排序,
   *   banner_name: banner名称,
   * }
   */
  const trackBannerClick = useCallback(
    (bannerName: string, moduleName = 'banner位', index = 0) => {
      try {
        trackEvent(
          {
            eventName: 'CampaignPageBanner_Click',
            properties: {
              page_name: '202603春促主会场',
              module_name: moduleName,
              pits_index: index,
              banner_name: bannerName,
              market_activity_id: marketActivityId,
              market_activity_name: '26年3月春促活动',
            },
          },
          [ProviderType.SENSORS],
        );
      } catch (error) {
        logger.error('Spring Sale banner click tracking error:', error);
      }
    },
    [trackEvent],
  );

  /**
   * @TrackType sensorsdata
   * @EventType CampaignPageBanner_Show
   * @EventDesc 202603春促主会场banner曝光
   * @EventParams {
   *   page_name: 页面名称,
   *   module_name: 模块名称,
   *   pits_index: 坑位排序,
   *   banner_name: banner名称,
   * }
   */
  const trackBannerShow = useCallback(
    (bannerName: string, moduleName = 'banner位', index = 0) => {
      try {
        trackEvent(
          {
            eventName: 'CampaignPageBanner_Show',
            properties: {
              page_name: '202603春促主会场',
              module_name: moduleName,
              pits_index: index,
              banner_name: bannerName,
              market_activity_id: marketActivityId,
              market_activity_name: '26年3月春促活动',
            },
          },
          [ProviderType.SENSORS],
        );
      } catch (error) {
        logger.error('Spring Sale banner show tracking error:', error);
      }
    },
    [trackEvent],
  );

  /**
   * @TrackType sensorsdata
   * @EventType CampaignPageProduct_Click
   * @EventDesc 202603春促主会场商品点击
   * @EventParams {
   *   page_name: 页面名称,
   *   module_name: 模块名称,
   *   pits_index: 坑位排序,
   *   product_id: 商品ID,
   *   product_name: 商品名称,
   *   tab_name: 商品导航tab名称,
   * }
   */
  const trackProductListPitsClick = useCallback(
    (item: any, index: number, moduleName: string, tabName?: string) => {
      try {
        const nowPriceInfo = getPriceInfo(userInfo, item, currency);
        const basePriceInfo = getPriceInfo(userInfo, item, env.NEXT_PUBLIC_DEFAULT_CURRENCY);

        const properties: any = {
          page_name: '202603春促主会场',
          module_name: moduleName,
          pits_index: index,
          product_id: `${item.id}`,
          product_name: item.short_name || item.name,
          // 保留额外字段以兼容其他数据分析需求
          product_sku: item.sku,
          business_sku: item.business_sku,
          shop_sku: item.shop_sku,
          product_price: parseFloat(nowPriceInfo.price || '0'),
          current_product_price: parseFloat(nowPriceInfo.sell_price || '0'),
          base_current_product_price: parseFloat(basePriceInfo.sell_price || '0'),
          dollar_exchange_rate: parseFloat(currency?.exchange_rate || '0'),
          currency: currency?.name || '',
        };

        // 如果提供了 tabName，添加到属性中
        if (tabName) {
          properties.tab_name = tabName;
        }

        // 添加活动标识
        properties.market_activity_id = marketActivityId;
        properties.market_activity_name = '26年3月春促活动';

        trackEvent(
          {
            eventName: 'CampaignPageProduct_Click',
            properties,
          },
          [ProviderType.SENSORS],
        );
      } catch (error) {
        logger.error('Spring Sale product click tracking error:', error);
      }
    },
    [trackEvent, currency, userInfo],
  );

  /**
   * @TrackType sensorsdata
   * @EventType CampaignPageProduct_Exp
   * @EventDesc 202603春促主会场商品曝光
   * @EventParams {
   *   page_name: 页面名称,
   *   module_name: 模块名称,
   *   pits_index: 坑位排序,
   *   product_id: 商品ID,
   *   product_name: 商品名称,
   *   tab_name: 商品导航tab名称,
   * }
   */
  const trackProductListPitsExposure = useCallback(
    (item: any, index: number, moduleName: string, tabName?: string) => {
      try {
        const nowPriceInfo = getPriceInfo(userInfo, item, currency);
        const basePriceInfo = getPriceInfo(userInfo, item, env.NEXT_PUBLIC_DEFAULT_CURRENCY);

        const properties: any = {
          page_name: '202603春促主会场',
          module_name: moduleName,
          pits_index: index,
          product_id: `${item.id}`,
          product_name: item.short_name || item.name,
          // 保留额外字段以兼容其他数据分析需求
          product_sku: item.sku,
          business_sku: item.business_sku,
          shop_sku: item.shop_sku,
          product_price: parseFloat(nowPriceInfo.price || '0'),
          current_product_price: parseFloat(nowPriceInfo.sell_price || '0'),
          base_current_product_price: parseFloat(basePriceInfo.sell_price || '0'),
          dollar_exchange_rate: parseFloat(currency?.exchange_rate || '0'),
          currency: currency?.name || '',
        };

        // 如果提供了 tabName，添加到属性中
        if (tabName) {
          properties.tab_name = tabName;
        }

        // 添加活动标识
        properties.market_activity_id = marketActivityId;
        properties.market_activity_name = '26年3月春促活动';

        trackEvent(
          {
            eventName: 'CampaignPageProduct_Exp',
            properties,
          },
          [ProviderType.SENSORS],
        );
      } catch (error) {
        logger.error('Spring Sale product exposure tracking error:', error);
      }
    },
    [trackEvent, currency, userInfo],
  );

  /**
   * @TrackType sensorsdata
   * @EventType CampaignPageAD_Click
   * @EventDesc 202603春促主会场广告位点击
   * @EventParams {
   *   page_name: 页面名称,
   *   module_name: 模块名称,
   *   tab_name: tab按钮名称,
   *   pits_index: 坑位排序
   * }
   */
  const trackAdClick = useCallback(
    (moduleName: string, tabName: string, index: number) => {
      try {
        const properties: any = {
          page_name: '202603春促主会场',
          module_name: moduleName,
          tab_name: tabName,
          pits_index: index,
          market_activity_id: marketActivityId,
          market_activity_name: '26年3月春促活动',
        };

        trackEvent(
          {
            eventName: 'CampaignPageAD_Click',
            properties,
          },
          [ProviderType.SENSORS],
        );
      } catch (error) {
        logger.error('Spring Sale ad click tracking error:', error);
      }
    },
    [trackEvent],
  );

  /**
   * @TrackType sensorsdata
   * @EventType CampaignPageAD_Exp
   * @EventDesc 202603春促主会场广告位曝光
   * @EventParams {
   *   page_name: 页面名称,
   *   module_name: 模块名称,
   *   tab_name: tab按钮名称,
   *   pits_index: 坑位排序,
   * }
   */
  const trackAdExp = useCallback(
    (moduleName: string, tabName: string, index: number) => {
      try {
        const properties: any = {
          page_name: '202603春促主会场',
          module_name: moduleName,
          tab_name: tabName,
          pits_index: index,
          market_activity_id: marketActivityId,
          market_activity_name: '26年3月春促活动',
        };

        trackEvent(
          {
            eventName: 'CampaignPageAD_Exp',
            properties,
          },
          [ProviderType.SENSORS],
        );
      } catch (error) {
        logger.error('Spring Sale ad exp tracking error:', error);
      }
    },
    [trackEvent],
  );

  // 定期更新滚动状态，避免页面跳转时丢失滚动信息（参考denim-glasses实现）
  useEffect(() => {
    const updateScrollState = () => {
      try {
        const viewAdvance = getCurrentScrollPercentage();
        const viewArea = getViewArea();
        lastScrollState.current = { viewAdvance, viewArea };
      } catch (error) {
        // 忽略错误，使用默认值
        logger.error('Error updating scroll state:', error);
      }
    };

    // 立即更新一次
    updateScrollState();

    // 监听滚动事件，实时更新滚动状态
    const handleScroll = () => {
      updateScrollState();
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return {
    trackPageView,
    trackPageLeave,
    trackNavTabClick,
    trackViewMoreClick,
    trackBannerClick,
    trackBannerShow,
    trackProductListPitsClick,
    trackProductListPitsExposure,
    trackAdClick,
    trackAdExp,
  };
}
